/** Pure helpers for building Purchase Order line rows in bulk.
 *
 *  Kept free of React so the expansion rules can be exercised directly. The
 *  server rejects a PO whose lines repeat an (item, size) pair, so every helper
 *  here appends only pairs that are not already present — a bulk action can
 *  never produce rows that fail validation the moment they appear.
 */

export const DEFAULT_LINE_QUANTITY = 1;

/** Identity of a line for duplicate purposes. A size-less item keys on "none",
 *  matching how the backend treats a null size. */
export const linePairKey = (itemId, sizeId) => `${itemId}::${sizeId ?? 'none'}`;

/** Sizes a user may actually pick: the Size dropdown filters out inactive ones,
 *  so bulk expansion must use the same list or it would create rows whose size
 *  cannot be seen or changed. */
export const activeSizes = (item) => (item?.sizes || []).filter((s) => s.status !== false);

export const sizeOption = (size) => ({ label: `${size.name} (${size.code})`, value: size.id });

/** A row the user has not filled in yet. Bulk actions drop these rather than
 *  leaving an empty row stranded above generated ones. */
export const isBlankLine = (line) => !line?.uniform_item;

export const existingPairKeys = (lines) => new Set(
  (lines || [])
    .filter((l) => l?.uniform_item?.value !== undefined && l?.uniform_item?.value !== null)
    .map((l) => linePairKey(l.uniform_item.value, l.size?.value ?? null)),
);

/** Every (item, size) row for one item that is not already present. An item with
 *  no active sizes contributes a single size-less row. */
export const buildLinesForItem = (item, existingLines, quantity = DEFAULT_LINE_QUANTITY) => {
  if (!item) return [];
  const taken = existingPairKeys(existingLines);
  const sizes = activeSizes(item);
  const candidates = sizes.length
    ? sizes.map((s) => ({ uniform_item: item, size: sizeOption(s), quantity }))
    : [{ uniform_item: item, size: null, quantity }];
  return candidates.filter(
    (line) => !taken.has(linePairKey(item.value, line.size?.value ?? null)),
  );
};

/** The same expansion across every item, in the order the items are given. */
export const buildLinesForAllItems = (items, existingLines, quantity = DEFAULT_LINE_QUANTITY) => {
  const taken = existingPairKeys(existingLines);
  const generated = [];
  (items || []).forEach((item) => {
    const sizes = activeSizes(item);
    const candidates = sizes.length
      ? sizes.map((s) => ({ uniform_item: item, size: sizeOption(s), quantity }))
      : [{ uniform_item: item, size: null, quantity }];
    candidates.forEach((line) => {
      const key = linePairKey(item.value, line.size?.value ?? null);
      if (taken.has(key)) return;
      taken.add(key); // guards against an item appearing twice in the source list
      generated.push(line);
    });
  });
  return generated;
};

/** Rows to keep when a bulk action runs: everything the user actually filled in. */
export const keepFilledLines = (lines) => (lines || []).filter((l) => !isBlankLine(l));
